import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import PropertyTypeFilters from './property_filters/property_type_filters';
import BuildingFilters from './property_filters/building_filters';
import HospitalityFilters from './property_filters/hospitality_filters';
import IndustrialFilters from './property_filters/industrial_filters';
import MultifamilyFilters from './property_filters/multifamily_filters';

import {
  filterLabelMappings,
  filterKeyMappings,
} from 'bundles/research/index/research_constants';

const industrialFiltersList = [
  'lotStabilization',
  'servedRailProvider',
  'lotBorderType',
  'availableRailProvider',
];

const pills = [
  {
    label: 'General',
    value: 'general',
  },
  {
    label: 'Industrial',
    value: 'industrial',
  },
  {
    label: 'Multifamily',
    value: 'multifamily',
  },
  {
    label: 'Hospitality',
    value: 'hospitality',
  },
];

export default class PropertyFilterGroup extends React.Component {
  static propTypes = {
    assetClasses: PropTypes.array.isRequired,
    availableRailProviders: PropTypes.array.isRequired,
    buildingClasses: PropTypes.array.isRequired,
    clearFiltersIfEmpty: PropTypes.func.isRequired,
    delayRetrieveRecords: PropTypes.func.isRequired,
    industrialFilter: PropTypes.object.isRequired,
    investmentTypes: PropTypes.array.isRequired,
    lotBorderTypes: PropTypes.array.isRequired,
    lotStabilizations: PropTypes.array.isRequired,
    multifamilyFilter: PropTypes.object.isRequired,
    propertyFilter: PropTypes.shape({
      lotUnits: PropTypes.string,
      apnNumber: PropTypes.string,
      maxBuildingSize: PropTypes.string,
      maxCeilingHeight: PropTypes.string,
      maxLotSize: PropTypes.string,
      maxMortgageAmount: PropTypes.string,
      maxMortgageRecordedDate: PropTypes.string,
      maxNumUnits: PropTypes.string,
      maxYearBuilt: PropTypes.string,
      minBuildingSize: PropTypes.string,
      minCeilingHeight: PropTypes.string,
      minLotSize: PropTypes.string,
      minMortgageAmount: PropTypes.string,
      minMortgageRecordedDate: PropTypes.string,
      minNumUnits: PropTypes.string,
      minYearBuilt: PropTypes.string,
    }).isRequired,
    selectedAssetClasses: PropTypes.array.isRequired,
    selectedBuildingClasses: PropTypes.array.isRequired,
    selectedInvestmentTypes: PropTypes.array.isRequired,
    servedRailProviders: PropTypes.array.isRequired,

    onAssetClassChange: PropTypes.func.isRequired,
    onBuildingClassChange: PropTypes.func.isRequired,
    onIndustrialFilterChange: PropTypes.func.isRequired,
    onInvestmentTypeChange: PropTypes.func.isRequired,
    onMultifamilyFilterChange: PropTypes.func.isRequired,
    onPropertyFilterChange: PropTypes.func.isRequired,
  };

  state = {
    activeFilter: 'general',
  };

  handleRetrieveRecords = () => {
    const { clearFiltersIfEmpty, delayRetrieveRecords } = this.props;
    clearFiltersIfEmpty();
    delayRetrieveRecords();
  };

  handlePropertyFilterChange = (e, name) => {
    const { propertyFilter, onPropertyFilterChange } = this.props;

    let value = '';
    if (e && e.target) {
      value = e.target.value;
    } else {
      value = e;
    }

    propertyFilter[name] = value || null;
    onPropertyFilterChange(propertyFilter);
    this.handleRetrieveRecords();
  };

  handleIndustrialFilterChange = (e, name, checkbox = false) => {
    let value = e ? e.target.value : '';
    if (checkbox) {
      value = e.target.checked;
    }

    const { industrialFilter, onIndustrialFilterChange } = this.props;

    if (industrialFiltersList.includes(name)) {
      industrialFilter[name] = industrialFilter[name] === value ? '' : value;
    } else {
      industrialFilter[name] = value;
    }
    onIndustrialFilterChange(industrialFilter);
    this.handleRetrieveRecords();
  };

  handleMultifamilyFilterChange = (e, name) => {
    const { multifamilyFilter, onMultifamilyFilterChange } = this.props;

    let value = '';
    if (e && e.target) {
      value = e.target.value;
    } else {
      value = e;
    }

    multifamilyFilter[name] = value || null;
    onMultifamilyFilterChange(multifamilyFilter);
    this.handleRetrieveRecords();
  };

  handleAssetClassChange = (e) => {
    const { onAssetClassChange } = this.props;
    onAssetClassChange(e.target.value, e.target.checked);
    this.handleRetrieveRecords();
  };

  handleBuildingClassChange = (e) => {
    const { onBuildingClassChange } = this.props;
    onBuildingClassChange(e.target.value, e.target.checked);
    this.handleRetrieveRecords();
  };

  handleInvestmentTypeChange = (e) => {
    const { onInvestmentTypeChange } = this.props;
    onInvestmentTypeChange(e.target.value, e.target.checked);
    this.handleRetrieveRecords();
  };

  render() {
    const { assetClasses, selectedAssetClasses } = this.props;

    return (
      <div className="d-flex flex-column gap-3">
        <PropertyTypeFilters
          assetClasses={assetClasses}
          selectedAssetClasses={selectedAssetClasses}
          onAssetClassChange={this.handleAssetClassChange}
        />
        {this.renderPropertyFilters()}
      </div>
    );
  }

  renderPropertyFilters() {
    const { activeFilter } = this.state;

    return (
      <div className="d-flex flex-column gap-2">
        <h6 className="d-none d-lg-block fw-semibold fs-large pb-1 border-bottom mt-3">
          Building Information
        </h6>
        <div className="nav nav-pills d-none d-lg-flex mt-1">
          {pills.map(pill => (
            <button
              className={classNames('nav-link', {
                active: pill.value === activeFilter,
              })}
              key={pill.label}
              type="button"
              onClick={() => this.setState({ activeFilter: pill.value })}
            >
              <span className="nav-link-text">{pill.label}</span>
            </button>
          ))}
        </div>
        <div className="property-group-container">{this.renderFilters()}</div>
      </div>
    );
  }

  renderFilters() {
    const { activeFilter } = this.state;

    switch (activeFilter) {
    case 'general':
      return <React.Fragment>{this.renderBuildingFilters()}</React.Fragment>;
    case 'multifamily':
      return (
        <React.Fragment>{this.renderMultifamilyFilters()}</React.Fragment>
      );
    case 'hospitality':
      return (
        <React.Fragment>{this.renderHospitalityFilters()}</React.Fragment>
      );
    case 'industrial':
      return (
        <React.Fragment>{this.renderIndustrialFilters()}</React.Fragment>
      );
    }
  }

  renderIndustrialFilters() {
    const {
      availableRailProviders,
      lotBorderTypes,
      lotStabilizations,
      industrialFilter,
      servedRailProviders,
    } = this.props;

    return (
      <IndustrialFilters
        availableRailProviders={availableRailProviders}
        filterKeyMappings={filterKeyMappings}
        filterLabelMappings={filterLabelMappings}
        industrialFilter={industrialFilter}
        lotBorderTypes={lotBorderTypes}
        lotStabilizations={lotStabilizations}
        servedRailProviders={servedRailProviders}
        onIndustrialFilterChange={this.handleIndustrialFilterChange}
      />
    );
  }

  renderMultifamilyFilters() {
    const { multifamilyFilter } = this.props;

    return (
      <MultifamilyFilters
        filterKeyMappings={filterKeyMappings}
        filterLabelMappings={filterLabelMappings}
        multifamilyFilter={multifamilyFilter}
        onMultifamilyFilterChange={this.handleMultifamilyFilterChange}
      />
    );
  }

  renderHospitalityFilters() {
    const { propertyFilter } = this.props;

    return (
      <HospitalityFilters
        filterKeyMappings={filterKeyMappings}
        filterLabelMappings={filterLabelMappings}
        propertyFilter={propertyFilter}
        onPropertyFilterChange={this.handlePropertyFilterChange}
      />
    );
  }

  renderBuildingFilters() {
    const buildingClassOptions = this.props.buildingClasses.map(building => ({
      id: building,
      value: building,
      label: building,
    }));

    const {
      investmentTypes,
      propertyFilter,
      selectedBuildingClasses,
      selectedInvestmentTypes,
    } = this.props;

    return (
      <BuildingFilters
        buildingClasses={buildingClassOptions}
        filterKeyMappings={filterKeyMappings}
        filterLabelMappings={filterLabelMappings}
        investmentTypes={investmentTypes}
        propertyFilter={propertyFilter}
        selectedBuildingClasses={selectedBuildingClasses}
        selectedInvestmentTypes={selectedInvestmentTypes}
        onBuildingClassChange={this.handleBuildingClassChange}
        onInvestmentTypeChange={this.handleInvestmentTypeChange}
        onPropertyFilterChange={this.handlePropertyFilterChange}
      />
    );
  }
}
